import React, { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { fetchOldAllMember } from "../Redux/Slice/AddMember";
import Loader from "../Components/Loader/Loader";

// --- Reusable Input Field Component ---
const InputFieldWithLabel = ({
  label,
  name,
  type = "text",
  placeholder,
  value,
  error,
  onChange,
}) => (
  <div className="col-span-12 md:col-span-6">
    <label className="block text-gray-900 text-sm font-bold mb-1">
      {label}
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        className="bg-white w-full border border-gray-300 p-2 rounded-md mt-1 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-normal text-gray-800"
        placeholder={placeholder || `Enter ${label}`}
      />
    </label>
    {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
  </div>
);

const BondTemplate = () => {
  const dispatch = useDispatch();
  const { MemberOldAllData, MemberOldAllLoading } = useSelector(
    (state) => state.AddMemberSlice
  );

  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formValues, setFormValues] = useState({
    employeeStartDate: "",
    companyName: "",
    employeeName: "",
    employeesFatherName: "",
    employeeAddress: "",
    internshipPeriod: "",
    fullTimeJobPeriod: "",
  });

  const [errors, setErrors] = useState({});
  const [generatedText, setGeneratedText] = useState("");
  const pdfContentRef = useRef(null);

  // Fetch employees on mount
  useEffect(() => {
    dispatch(fetchOldAllMember());
  }, [dispatch]);

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Populate data when employee is selected
  const handleEmployeeSelect = (e) => {
    const empId = e.target.value;
    setSelectedEmpId(empId);
    setGeneratedText("");

    if (!empId) {
      resetForm();
      return;
    }

    const emp = MemberOldAllData?.data?.find((el) => el._id === empId);
    if (emp) {
      setFormValues({
        employeeStartDate: formatDateForInput(emp.BondStartDate || emp.JoiningDate) || "",
        companyName: emp.Firm || "",
        employeeName: emp.Name || "",
        employeesFatherName: "",
        employeeAddress: emp.Address || "",
        internshipPeriod: emp.internshiptime || "",
        fullTimeJobPeriod: emp.BondTime || "",
      });
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (newErrors[name]) delete newErrors[name];
      return newErrors;
    });
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formValues.employeeStartDate)
      newErrors.employeeStartDate = "Start Date is required";
    if (!formValues.companyName)
      newErrors.companyName = "Company Name is required";
    if (!formValues.employeeName)
      newErrors.employeeName = "Employee Name is required";
    if (!formValues.employeesFatherName)
      newErrors.employeesFatherName = "Father's Name is required";
    if (!formValues.employeeAddress)
      newErrors.employeeAddress = "Employee Address is required";
    if (!formValues.internshipPeriod || isNaN(formValues.internshipPeriod))
      newErrors.internshipPeriod =
        "Valid months for Internship Period is required";
    if (!formValues.fullTimeJobPeriod || isNaN(formValues.fullTimeJobPeriod))
      newErrors.fullTimeJobPeriod =
        "Valid months for Full-Time Period is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateTemplateText = (data) => {
    const internshipMonths = parseInt(data.internshipPeriod) || 0;
    const fullTimeMonths = parseInt(data.fullTimeJobPeriod) || 0;
    const totalMonths = internshipMonths + fullTimeMonths;

    const formattedDate = new Date(data.employeeStartDate).toLocaleDateString(
      "en-GB",
      {
        day: "numeric",
        month: "long",
        year: "numeric",
      }
    );

    return `
<h3 style="text-align: center; font-weight: bold; margin-top : 0; padding-top: 0; margin-bottom: 10px; font-size: 1rem;">
  TRAINING CUM EMPLOYMENT AGREEMENT FOR A SPECIFIC BOND
</h3>

<p>
This training-cum-employment agreement (“Agreement”) is executed on **${formattedDate}** at Surat:
</p>

BETWEEN 

**${data.companyName}**, a company incorporated under the provisions of the companies act, 
1956 and having its registered office situated at 402, Empire State Building, Near Udhna 
Darwaja, Ring Road, Surat-395002. (Hereinafter referred to as “the company”) which 
expression shall, unless repugnant to the context and meaning thereof, be deemed to 
include administrators and permitted assignees of the FIRST PART

AND 

**${data.employeeName}** son of **${data.employeesFatherName}** permanently residing at **${data.employeeAddress}** 
(herein after referred to as “the Employee”) of the SECOND PART
The company and employee shall hereinafter be collectively referred to as “the Parties”
and where the context permits, shall individually be referred to as the “the Party”.

WHEREAS the employee had applied for suitable employment in the company, management 
has now decided to position the employee on the designation of Software Engineer and in 
said lieu the company and the employee has mutually agreed on the terms and conditions 
mentioned under the T & C Engagement Letter where in the date of joining shall be affected 
from **${formattedDate}** however the Employee understands that the Agreement is valid
from the date of signing. WHEREAS the employee had earlier been associated with the 
company for suitable employment, and after the initial engagement, due to changing 
business dynamics there has been a revision in roles and responsibilities entrusted, and 
considering its accountability and criticalness, after mutual consent, and as formalized with 
this agreement and the employee has mutually agreed to enter this fresh bond/ agreement 
on the terms and conditions mentioned herein, and the Employee understands that the 
Agreement is valid from said date.
 

AND WHEREAS in terms of this fresh renewed engagement, the Employee is required to 
provide an independent undertaking, as herein appearing to back up the obligation and 
liabilities of the Employee as a condition of his/her employment.
NOW, THEREFORE, THIS AGREEMENT WITNESSETH AND THE PARTIES HERETO AGREE AS 
UNDER:

1. The Employee understands and acknowledges that the Company would be spending lot of 
monetary resources on the training and induction of the Employee on various business 
processes, which cost is identifiable, have already been explained to the Employee by the 
Company and are inclusive of the following components / elements:

a. Proportionate costs incurred by the Company on deputing and dedicating a team of 
employees or professionals for training the Employee, including travelling, boarding and
</p>
<br/>

<p style="margin-top : 50px;">
lodging expenses, emoluments/fees paid by the Company or its parent company to such 
trainers.

b. Other costs such as compensation for the remote training. coaching and mentoring that 
has occurred by the associate counterparts of the Company. The Employee further 
understands and acknowledges that the company is put to loss (in terms of the quantified 
cost it incurs in the process of training of the Employee) and also suffers opportunity cost in 
terms of loss of business opportunities, negative growth in business if the Employee leaves 
its employments (which cannot be appropriately quantified) and to offset all such costs, the 
Employee, after being explained the rationale and the contents of this Agreement by the 
Company on three different occasions as specified hereunder, and after consulting family 
members, friends, and advisor(s) in the matter, by his / her own will, discretion and 
judgment, agrees and undertakes to serve the Company continuously for a minimum period 
of **${totalMonths} Month** **([${data.internshipPeriod} months Internship + ${data.fullTimeJobPeriod} Month Full-Time Service])**, "The Service 
Period") from the date of his/her appointment letter and shall not leave the service of the 
Company before successful completion of the said Service Period. During the said Service 
Period, the Employee shall not seek employment, or enter into employment of any other 
Employer or directly or indirectly engage in any business including as that of the Company.


2. The employee undertakes to make good the loss, probable loss and incidental damage(s) 
suffered to be suffered by the Company in the event of breach of this bond/ agreement and 
in said lieu the employee hereby promises to indemnify the company with an amount equal 
to twelve-month salary (considering the latest revision & last drawn emoluments) 
whichever is more, towards the performance of the terms of this Agreement.


The parties agree that the Company shall have a right to demand the above referred 
amount from the employee same as mentioned in this Para, should the Employee breach 
the terms of this Agreement. The decision of the Company, of the Employee being in the 
breath of terms and conditions of the Agreement shall be Final.


3. During the Service Period, the Employee shall be under an obligation to work with utmost 
professional competency and dedication to serve the Company. Factors such as change in 
nature of job profile, place of work, timings of work, change in span of responsibilities, 
assignment to another associate entity concern / company will not invalidate this 
Agreement though the Company assures the Employee that any changes seeking to impact 
the Employee will be taken only after taking into consideration the Employee's views and an 
objective evaluation of the Employee's position, benefits and rights. The decision of the 
Company shall be final and binding upon the Employee. And, notice period of the employee 
shall be affective and will be made into consideration only after completion of bond period, 
no prior intimation on exit or resignation will bind it.
<div style="page-break-inside: avoid; margin-bottom: 20px;">
4. The Employee unconditionally agrees to serve the Company and its business interests and 
stakeholders with full professional competency and dedication. The Employee shall not 
disclose, share and transfer confidential or sensitive business information, whether for 
commercial gain or not, to rivals or unauthorized individuals and entities, except with prior 
authorization from the Company. The Employee shall not attempt to form unlawful and 
unauthorized union of people with the intention of lowering the productivity and quality of 
work or with the intention to lowering the dignity of the Company among its external environments.
</div>
</p>

<br/>

<p >
5. The Employee agrees to follow the terms and conditions of the Employee Service
regulations of the Company. The Parties agree that the Company may, without giving any
prior notice, amend and / or alter any or all of the provisions of the Employee Service
regulations from time to time in which case, the amended regulations shall apply to the
Employee.

6. The Employee confirms that he/she will comply with all office orders and directions
issued by the Company from time to time.

7. Sharing of confidential or sensitive business information, whether for commercial gain or
not, to rivals or unauthorized individuals and entities will not only make the Employee liable
for prosecution under the relevant Indian Laws but also make him liable to pay damages as
determined by the Management of the Company based on a fair estimation of the economic
damages caused to the Company by the Employee's irresponsible action (s). Sharing of
confidential or sensitive information shall comprise of, however not limited to the following.
The Company reserves the right to modify this list and expand the contents to cover a wider
ambit of violations.
<div style="margin-left: 60px;">
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; a. Sharing of information about the clients / customers / supplier of the
Company, which is available to the Employee during the course of his/her
engagement with the Company and where the information concerned is not 
present in the public domain.

&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; b. Disclosure of the list of clients of the Company, pricing policy. internal
marketing/sales/finance/ HR strategy, phone lists of employees and similar
documents.
</div>
8. If at any time during tenure of his/her employment with the Company, the Employee is
found guilty of:
<div style="margin-left: 60px;">
a. Any misconduct including as defined under the Employee Service Regulations, or

b. Any wilful breach or continuous neglect of the terms of the Agreement, or

c. Dereliction of his/her duties
</div>
The Company may, without any notice, determine his/her employment with the Company. 
The Employee shall be deemed to have brought about such a situation by his / her 
misconduct compelling the Company to put an end to his/her employment and he/she shall, 
therefore, continue to be liable for all losses / damages in respect thereof and pay 
compensation to the Company in terms of this Agreement. This clause does not apply if the 
Employee simply does not perform or have the ability to perform his duties. The intent of 
the clause is to prevent an employee from purposely not trying to perform his duties as 
opposed to not having the ability to perform his duties. Company recognizes that not all 
Employees will be able to perform their duties to the level that will satisfy the Company and, 
in those situations, the Employee will simply be asked to transit out of the Organization and 
his cheque and "training agreement" will be returned to the Employee

</p>
<br/>

<p style="margin-top : 40px;">
9. If the Employee leaves the employment of the Company or brings about a situation as
referred in Para no.: 8 above compelling the Company to terminate his/her services during
the validity of the Service Period, the Employee unconditionally agrees to pay, on demand,
to the Company, a sum of INR 50000/- as pre-estimated liquidated damages as a
compensation for the breach of the terms of this Agreement which would be in addition to
the right of the Company to claim the amount as referred in terms of Para no.: 02 above.
The Company shall have a right to issue a public advertisement in journals, newspapers and
media notifying the general public about the Employee's breach of the terms and
employment and notify the Management of the new entity that the Employee seeks to join
upon after leaving the Company.

10. The Parties agree that the Employee shall also be liable for all the expenses incurred by
the Company in initiating the legal process against the Employee for the breach of the terms
of this Agreement, if required.

11. All the terms and conditions of service of the Employee as contained in his/her
Employee Engagement / Appointment Letter, read with the Employees Service Rules shall
remain valid and in force except to the extent such terms are at variance with the terms of
this Agreement in which case, the terms of this Agreement shall prevail.

12. Neither the waiver by the Company of a breach of, or a default under, any of the
provisions of this Agreement, nor the failure of the Company on one or more occasions, to
enforce any of the provisions of this Agreement or to exercise any right or privilege
hereunder shall thereafter be construed as a waiver of any subsequent breach or default of
a similar nature, or as waiver of any of such provisions, rights, or privilege hereunder.

13. The Parties agree that the rights, duties and obligations hereunder are personal in
nature and the Employees shall not assign his rights, assign his responsibilities / obligations
receiver. The Parties, however, agree that the Company may assign, all or any part of its
rights and obligations pursuant to this Agreement to any person or entity in which case, the
Company's rights and obligations will bind and insure to the benefit of its successor and the
assigns. Assignment, if any, made by the Company shall be without imposing any additional
obligation on the Employee.

14.Any notice to be given by any Party under this Agreement to the other must be in writing
in English language by any one of the following ways:
<div style="margin-left: 60px;">
a. By hand delivery, the receiver acknowledges the receipt.

b. By e-mail provided the receiver acknowledges the receipt.
</div>
Receipt generated by the computer system of the receiver of his having read the message
and/or the reply shall be sufficient service and acknowledgement at the receiver's end.
<div style="margin-left: 60px;">
c. By Courier service / registered post to the last known postal address of the other
Party. The Employee shall inform any change in his/her postal address within two
days of the change failing which all communication sent to his / her address in the
official records of the Company shall be deemed to be properly served.

</div>
</p>
<br/>

<p style="margin-top : 40px;">
15. The remedies available to the Company under this Agreement shall not, in any manner 
whatsoever, mitigate the rights available to the Company under any other Agreement(s) to 
any third individual and / or party binding upon them.

16. The Parties warrant that by virtue of entering into the Contract, they will not be in 
breach of any express or implied term(s) of any other Contract with or any other obligation 
(s) to any third individual and / or party binding upon them.

17. If any Clause of this Contract is prohibited by or is invalid under any law, that clause will 
be ineffective only to the extent of the prohibition or invalidity. The remainder of the 
affected clause and all other clauses of this Contract shall be unaffected by the invalidity.

18. This Contract constitutes the entire Agreement between the Parties and revokes and 
supersedes all previous discussions / correspondences. And agreement between the Parties, 
if any. concerning the matters covered herein whether oral or implied. This Contract shall 
not be changed or modified except by written amendment duly agreed by the Parties.

19. Save as provided in Clause no.: 13 hereof, the courts at Surat shall exercise all 
jurisdictions in relation to any legal dispute arising between the Parties in terms of this 
Agreement.

IN WITNESS WHEREOF the Parties, to this Agreement have signed on the date, month and 
year first mentioned herein above.
</p>
        `;
  };

  const handleModalSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const text = generateTemplateText(formValues);
      setGeneratedText(text);
      setErrors({});
      setIsModalOpen(false);
    }
  };

  const resetForm = () => {
    setFormValues({
      employeeStartDate: "",
      companyName: "",
      employeeName: "",
      employeesFatherName: "",
      employeeAddress: "",
      internshipPeriod: "",
      fullTimeJobPeriod: "",
    });
    setErrors({});
    setGeneratedText("");
    setSelectedEmpId("");
  };

  const handleDownloadPdf = async () => {
    const element = pdfContentRef.current;
    if (!element) {
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = 210;
    const pageHeight = 297;
    const marginTop = 15;
    const marginBottom = 15;
    const contentWidth = pageWidth - 15;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL("image/png");
    const imgProps = pdf.getImageProperties(imgData);
    const imgHeight = (imgProps.height * contentWidth) / imgProps.width;

    let heightLeft = imgHeight;
    let position = marginTop;

    pdf.addImage(imgData, "PNG", 15, position, contentWidth, imgHeight);
    heightLeft -= pageHeight - marginTop - marginBottom;

    while (heightLeft > 0) {
      pdf.addPage();
      position = -(imgHeight - heightLeft);
      pdf.addImage(imgData, "PNG", 15, position, contentWidth, imgHeight);
      heightLeft -= pageHeight - marginTop - marginBottom;
    }

    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
    }

    pdf.save(`Bond_Agreement_${formValues.employeeName || "Employee"}.pdf`);
  };

  const getFormattedHtml = (text) => {
    if (!text) return "";
    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\n/g, "<br />");
  };

  if (MemberOldAllLoading) return <Loader />;

  return (
    <div className="min-h-screen p-4 lg:p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl font-bold bg-[#122A47] text-white px-4 py-2 inline-block rounded mb-6">
          EMPLOYEE AGREEMENT (BOND)
        </h1>

        {/* STEP 1: Employee Selection Card */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#122A47] mb-4">
            Step 1: Select Employee
          </h2>
          <div className="grid grid-cols-12 gap-4 items-center">
            <div className="col-span-12 md:col-span-8">
              <select
                value={selectedEmpId}
                onChange={handleEmployeeSelect}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-[#122A47] focus:border-[#122A47] bg-white text-gray-900 font-medium"
              >
                <option value="">-- Choose Employee / Intern --</option>
                {MemberOldAllData?.data?.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.Name} {emp.Role ? `(${emp.Role})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-12 md:col-span-4 flex gap-2">
              <button
                disabled={!selectedEmpId}
                onClick={() => setIsModalOpen(true)}
                className={`flex-1 text-white py-2 px-4 rounded-md transition-colors font-semibold ${selectedEmpId
                  ? "bg-[#122A47] hover:bg-[#0c2545]"
                  : "bg-gray-400 cursor-not-allowed"
                  }`}
              >
                Fill Details & Open Template
              </button>
              <button
                onClick={resetForm}
                className="bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition-colors font-semibold"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* STEP 2: The Modal (Popup Form) */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-[#122A47] text-white rounded-t-lg">
                <h3 className="text-lg font-bold">Edit Bond Agreement Details</h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-white hover:text-gray-300 text-2xl font-bold"
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleModalSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-12 gap-4">
                  <InputFieldWithLabel
                    label="Employee Name (from Database)"
                    name="employeeName"
                    value={formValues.employeeName}
                    error={errors.employeeName}
                    onChange={handleChange}
                  />

                  <InputFieldWithLabel
                    label="Employee's Father's Name"
                    name="employeesFatherName"
                    placeholder="Enter Father's Full Name"
                    value={formValues.employeesFatherName}
                    error={errors.employeesFatherName}
                    onChange={handleChange}
                  />

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-gray-900 text-sm font-bold mb-1">
                      Company Name
                      <select
                        name="companyName"
                        value={formValues.companyName}
                        onChange={handleChange}
                        className="bg-white w-full border border-gray-300 p-2 rounded-md mt-1 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-normal text-gray-800"
                      >
                        <option value="">-- Select Company --</option>
                        <option value="Dynamic Software">Dynamic Software</option>
                        <option value="Dynamic Softlink Pvt Ltd.">Dynamic Softlink Pvt Ltd.</option>
                        {formValues.companyName &&
                          formValues.companyName !== "Dynamic Software" &&
                          formValues.companyName !== "Dynamic Softlink Pvt Ltd." && (
                            <option value={formValues.companyName}>
                              {formValues.companyName} (from DB)
                            </option>
                          )}
                      </select>
                    </label>
                    {errors.companyName && (
                      <p className="text-red-600 text-sm mt-1">{errors.companyName}</p>
                    )}
                  </div>

                  <InputFieldWithLabel
                    label="Agreement Start Date"
                    name="employeeStartDate"
                    type="date"
                    value={formValues.employeeStartDate}
                    error={errors.employeeStartDate}
                    onChange={handleChange}
                  />

                  <InputFieldWithLabel
                    label="Employee Residential Address (from Database)"
                    name="employeeAddress"
                    value={formValues.employeeAddress}
                    error={errors.employeeAddress}
                    onChange={handleChange}
                  />

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-gray-900 text-sm font-bold mb-1">
                      Internship/Training Period (in Months)
                      <select
                        name="internshipPeriod"
                        value={formValues.internshipPeriod}
                        onChange={handleChange}
                        className="bg-white w-full border border-gray-300 p-2 rounded-md mt-1 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-normal text-gray-800"
                      >
                        <option value="">-- Select Period --</option>
                        {[1, 2, 3, 4, 5, 6].map((m) => (
                          <option key={m} value={m}>
                            {m} {m === 1 ? "Month" : "Months"}
                          </option>
                        ))}
                        {formValues.internshipPeriod &&
                          ![1, 2, 3, 4, 5, 6].includes(Number(formValues.internshipPeriod)) && (
                            <option value={formValues.internshipPeriod}>
                              {formValues.internshipPeriod} Months (from DB)
                            </option>
                          )}
                      </select>
                    </label>
                    {errors.internshipPeriod && (
                      <p className="text-red-600 text-sm mt-1">{errors.internshipPeriod}</p>
                    )}
                  </div>

                  <InputFieldWithLabel
                    label="Full-Time Period (Months)"
                    name="fullTimeJobPeriod"
                    type="number"
                    value={formValues.fullTimeJobPeriod}
                    error={errors.fullTimeJobPeriod}
                    onChange={handleChange}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-[#122A47] text-white py-2 px-4 rounded-md hover:bg-[#0c2545] transition-colors font-medium"
                  >
                    Generate Document
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* STEP 3: Preview and Download Panel */}
        {generatedText && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6 mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#122A47]">
                Agreement Preview
              </h2>
              <button
                onClick={handleDownloadPdf}
                className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors font-semibold"
              >
                Download PDF
              </button>
            </div>

            <div className="max-w-full overflow-x-auto bg-gray-100 p-4 rounded-lg border border-gray-300">
              <div
                ref={pdfContentRef}
                className="bg-white shadow-lg mx-auto"
                style={{
                  width: "180mm",
                  minHeight: "270mm",
                  padding: "15mm",
                  boxSizing: "border-box",
                }}
              >
                <div
                  className="font-sans text-[13px] text-left leading-relaxed text-gray-900"
                  dangerouslySetInnerHTML={{
                    __html: getFormattedHtml(generatedText),
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BondTemplate;
